/**
 * Knowledge Intents Table
 * One row per intent (queries grouped by their shared `intent`). Each row shows what the intent
 * serves (its promoted queries, and how many are switched on) and carries one switch that pauses
 * or resumes its promoted queries; it cannot promote. A click opens a details side panel listing
 * that intent's queries.
 */

import { useMemo, useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { DataTable } from '@/components/ui/data-table';
import { useSetKnowledgeIntentEnabled } from '@/hooks/use-promoted-blueprints';
import { usePermissions } from '@/hooks/use-permissions';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { getKnowledgeIntentsColumns } from './knowledge-intents-columns';
import { groupKnowledgeByIntent } from '../utils';
import type { KnowledgeBlueprint, KnowledgeIntentGroup } from '../types';

export function KnowledgeIntentsTable({
  knowledgeBlueprints,
  isLoading,
  onRowClick
}: {
  knowledgeBlueprints: KnowledgeBlueprint[];
  isLoading: boolean;
  onRowClick: (group: KnowledgeIntentGroup) => void;
}) {
  const { can } = usePermissions();
  const canEdit = can('edit', 'promoted-blueprint');
  const setIntentEnabledMutation = useSetKnowledgeIntentEnabled();
  const [pendingIntent, setPendingIntent] = useState<string | null>(null);

  const groups = useMemo(() => groupKnowledgeByIntent(knowledgeBlueprints ?? []), [knowledgeBlueprints]);

  const handleToggleEnabled = async (group: KnowledgeIntentGroup, enabled: boolean) => {
    const queryKey = ['promoted-blueprints', 'knowledge'];
    // Optimistic: flip every query under this intent.
    queryClient.setQueryData<KnowledgeBlueprint[]>(queryKey, (prev) =>
      (prev ?? []).map((item) => (item.intent === group.intent ? { ...item, enabled } : item))
    );
    setPendingIntent(group.intent);
    try {
      await setIntentEnabledMutation.mutateAsync({ intent: group.intent, enabled });
      toast.success(enabled ? 'Intent resumed: its promoted answers serve again' : 'Intent paused: its answers no longer serve');
    } catch (error) {
      // Rollback
      queryClient.setQueryData<KnowledgeBlueprint[]>(queryKey, (prev) =>
        (prev ?? []).map((item) => (item.intent === group.intent ? { ...item, enabled: !enabled } : item))
      );
      toast.error('Failed to update intent');
      console.error('Toggle knowledge intent error:', error);
    } finally {
      setPendingIntent(null);
      invalidateQueries(queryKey);
    }
  };

  const columns = getKnowledgeIntentsColumns({ canEdit, pendingIntent, onToggleEnabled: handleToggleEnabled });

  return (
    <DataTable
      columns={columns}
      data={groups}
      isLoading={isLoading}
      enableSorting={true}
      enableFiltering={true}
      enablePagination={true}
      enableColumnVisibility={false}
      enableRowSelection={false}
      pageSize={200}
      pageSizeOptions={[10, 20, 50]}
      emptyMessage='No knowledge intents yet.'
      onRowClick={onRowClick}
    />
  );
}
